import {
  Hidden,
  Columns,
  Column,
  TabsProvider,
  Tabs,
  Tab,
  MenuRenderer,
  ButtonIcon,
  IconRenderer,
  MenuItem,
  Dropdown,
  useResponsiveValue,
  Text,
  Inline,
  Box,
} from 'braid-design-system';
import { useMemo } from 'react';

import * as styles from './IntegrationHealthGroupBy.css';
import { ThreeDotsSvg } from './ThreeDotsSvg.tsx';
import {
  type GroupBy,
  filterTypesMap,
  filterTypes,
  groupByOptions,
} from './config.ts';

export const IntegrationHealthGroupBy = ({
  groupBy,
  setGroupBy,
}: {
  groupBy: GroupBy;
  setGroupBy: (g: GroupBy) => void;
}) => {
  const tabsKey =
    useResponsiveValue()({
      mobile: 'mobile',
      tablet: 'tablet-and-above',
    }) ?? 'ssr';

  const groupByTabsToDisplay = useMemo(() => {
    const first5 = groupByOptions.slice(0, 5);
    const currentIndex = groupByOptions.findIndex((type) => type === groupBy);

    return currentIndex >= 5
      ? [...first5.slice(0, 4), groupByOptions[currentIndex]]
      : first5;
  }, [groupBy]);

  const remainingGroupByOptions = useMemo(() => {
    const shownTypes = new Set(groupByTabsToDisplay);
    return groupByOptions.filter((type) => !shownTypes.has(type));
  }, [groupByTabsToDisplay]);

  return (
    <>
      <Hidden below="tablet">
        <Box
          className={styles.customDividerBorder}
          gap="small"
          alignItems="flexStart"
          paddingRight="small"
        >
          <Text weight="strong" size="small">
            Group by
          </Text>

          <Inline space="none">
            <Columns space="medium">
              <Column width="content">
                <TabsProvider
                  key={tabsKey}
                  id="group-by"
                  selectedItem={groupBy}
                  onChange={(_, item) => setGroupBy(item as GroupBy)}
                >
                  <Tabs label="Group by" size="small" divider="none">
                    <Tab item="None">All</Tab>
                    {groupByTabsToDisplay.map((type) => (
                      <Tab key={type} item={type}>
                        {filterTypesMap.get(type)!.label}
                      </Tab>
                    ))}
                  </Tabs>
                </TabsProvider>
              </Column>
              <Column width="content">
                <MenuRenderer
                  offsetSpace="small"
                  align="right"
                  trigger={(triggerProps) => (
                    <ButtonIcon
                      variant="transparent"
                      icon={
                        <Text>
                          <IconRenderer>{ThreeDotsSvg}</IconRenderer>
                        </Text>
                      }
                      label="More"
                      id="group-by-more"
                      {...triggerProps}
                    />
                  )}
                >
                  {remainingGroupByOptions.map((type) => (
                    <MenuItem key={type} onClick={() => setGroupBy(type)}>
                      {filterTypesMap.get(type)!.label}
                    </MenuItem>
                  ))}
                </MenuRenderer>
              </Column>
            </Columns>
          </Inline>
        </Box>
      </Hidden>
      <Hidden above="mobile">
        <Dropdown
          id="group-by-dropdown"
          label="Group by"
          placeholder="Group by"
          value={groupBy}
          onChange={(e) => setGroupBy(e.currentTarget.value as GroupBy)}
        >
          {filterTypes.map(({ type, label }) => (
            <option key={type} value={type}>
              {label}
            </option>
          ))}
        </Dropdown>
      </Hidden>
    </>
  );
};
